import type { RefObject, ChangeEvent } from 'react'
import { ErrorBanner } from '../ui/index.jsx'

interface CompanyConfig {
  title?: string
  subtitle?: string
}

interface StepConfig {
  company?: CompanyConfig
}

interface CompanyStepProps {
  stepConfig: StepConfig
  companyName: string
  setName: (name: string) => void
  logoPreview: string | null
  logoUploading: boolean
  logoError: string
  fileRef: RefObject<HTMLInputElement>
  handleLogoChange: (e: ChangeEvent<HTMLInputElement>) => void
  saveCompany: () => void
  error: string
  loading: boolean
  t: (key: string) => string
}

export default function CompanyStep({
  stepConfig, companyName, setName, logoPreview, logoUploading, logoError,
  fileRef, handleLogoChange, saveCompany, error, loading, t,
}: CompanyStepProps) {
  return (
    <div>
      <h2 style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
        {stepConfig.company?.title || t('yourCompany')}
      </h2>
      <p style={{ fontSize: 'var(--text-base)', color: 'var(--text-muted)', marginBottom: 32 }}>
        {stepConfig.company?.subtitle || t('companyNameRequired')}
      </p>

      {/* Logo upload */}
      <div style={{ marginBottom: 28 }}>
        <label style={{ fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 10 }}>
          {t('logoOptional')}
        </label>
        <div
          role="button"
          tabIndex={0}
          aria-label={t('logoOptional')}
          onClick={() => !logoUploading && fileRef.current?.click()}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!logoUploading) fileRef.current?.click() } }}
          style={{
            width: 100, height: 100, borderRadius: 12,
            border: '2px dashed var(--border-default)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: logoUploading ? 'wait' : 'pointer', overflow: 'hidden',
            background: logoPreview ? 'transparent' : 'var(--bg-elevated)',
            transition: 'border-color 0.15s', position: 'relative',
          }}
          onMouseEnter={e => { if (!logoUploading) e.currentTarget.style.borderColor = 'var(--accent)' }}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
        >
          {logoPreview ? (
            <img src={logoPreview} alt="Logo" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: logoUploading ? 0.5 : 1 }} />
          ) : (
            <span style={{ fontSize: 'var(--text-3xl)' }}>🖼</span>
          )}
          {logoUploading && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', background: 'rgba(0,0,0,0.4)',
              fontSize: 'var(--text-xs)', color: 'var(--text-primary)', fontWeight: 600,
            }}>
              {t('uploading')}
            </div>
          )}
        </div>
        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.gif,.svg,.webp,image/png,image/jpeg,image/gif,image/svg+xml,image/webp" onChange={handleLogoChange} style={{ display: 'none' }} />
        <button
          onClick={() => !logoUploading && fileRef.current?.click()}
          disabled={logoUploading}
          style={{ marginTop: 8, background: 'none', border: 'none', fontSize: 'var(--text-caption)',
            color: 'var(--accent)', cursor: logoUploading ? 'wait' : 'pointer', padding: 0 }}
        >
          {logoUploading ? t('uploading') : logoPreview ? t('changeLogo') : t('uploadLogo')}
        </button>
        {logoError && (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--warning)', marginTop: 4, maxWidth: 280 }}>{logoError}</p>
        )}
      </div>

      {/* Company name */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
          {t('companyNameLabel')} *
        </label>
        <input
          type="text"
          value={companyName}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Oslo Opera House"
          autoFocus
          onKeyDown={e => e.key === 'Enter' && saveCompany()}
          style={{
            width: '100%', padding: '11px 14px', fontSize: 'var(--text-md)', boxSizing: 'border-box',
            background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
            borderRadius: 8, color: 'var(--text-primary)',
            outline: 'none',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--accent)'}
          onBlur={e => e.target.style.borderColor = 'var(--border-default)'}
        />
      </div>

      <ErrorBanner style={{ fontSize: 'var(--text-caption)', marginBottom: 12 }}>{error}</ErrorBanner>

      <button
        onClick={saveCompany}
        disabled={loading}
        style={{
          width: '100%', padding: '13px', marginTop: 12, borderRadius: 10,
          background: loading ? 'var(--bg-elevated)' : 'var(--accent)',
          color: loading ? 'var(--text-muted)' : 'var(--text-inverse)',
          border: 'none', fontSize: 'var(--text-md)', fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
        }}
      >
        {loading ? t('saving') : t('continueArrow')}
      </button>
    </div>
  )
}
